import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Youtube, Music2, Send, Trophy } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionTitle } from "@/components/SectionTitle";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { IMAGES } from "@/lib/images";
import { useSeo } from "@/lib/useSeo";
import { getSettings, submitContact } from "@/lib/db";

const SUBJECTS = ["Ποδόσφαιρο", "Μπάσκετ", "Συνεργασία", "Άλλο"];

export default function Contact() {
  const [settings, setSettings] = useState({});
  useSeo({ title: "Επικοινωνία", description: "Επικοινωνήστε με τη Ρεάλ Δυτικής Αττικής — φόρμα επικοινωνίας, τηλέφωνο, email και στοιχεία γηπέδων." });
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getSettings().then((d) => setSettings(d)).catch(() => {});
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Συμπληρώστε το ονοματεπώνυμό σας";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Μη έγκυρο email";
    if (!form.subject) e.subject = "Επιλέξτε θέμα";
    if (form.message.trim().length < 10) e.message = "Το μήνυμα πρέπει να έχει τουλάχιστον 10 χαρακτήρες";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Το μήνυμά σας εστάλη με επιτυχία!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setErrors({});
    } catch {
      toast.error("Κάτι πήγε στραβά. Προσπαθήστε ξανά.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = (k) => ({
    value: form[k],
    onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })),
  });

  const inputCls =
    "w-full rounded-lg border border-white/15 bg-royal/60 px-4 py-3 text-ice placeholder-ice/40 outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/40";

  const socials = [
    { key: "facebook", Icon: Facebook }, { key: "instagram", Icon: Instagram },
    { key: "youtube", Icon: Youtube }, { key: "tiktok", Icon: Music2 },
  ];

  return (
    <div>
      {/* BANNER */}
      <section className="relative flex h-[40vh] min-h-[300px] items-end overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.contactBanner})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/80 to-navy/40" />
        <div className="club-container relative z-10 pb-12">
          <p className="font-display text-sm font-600 uppercase tracking-[0.3em] text-gold">Ελάτε σε επαφή</p>
          <h1 className="font-heading mt-2 text-5xl font-900 uppercase tracking-tight text-white sm:text-7xl">
            Επικοινωνία
          </h1>
        </div>
      </section>

      <section className="club-container py-16 sm:py-24">
        <div className="grid gap-10 lg:grid-cols-2">
          {/* FORM */}
          <Reveal>
            <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-royal/30 p-7 sm:p-9" data-testid="contact-form" noValidate>
              <h2 className="font-heading text-2xl font-800 text-white">Στείλτε μας μήνυμα</h2>
              <div className="mt-6 space-y-5">
                <div>
                  <label className="mb-1.5 block text-sm font-600 text-ice">Ονοματεπώνυμο</label>
                  <input {...field("name")} className={inputCls} placeholder="π.χ. Γιώργος Παπαδόπουλος" data-testid="contact-name" />
                  {errors.name && <p className="mt-1 text-sm text-red-400" data-testid="error-name">{errors.name}</p>}
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-600 text-ice">Email</label>
                    <input {...field("email")} type="email" className={inputCls} placeholder="email@example.com" data-testid="contact-email" />
                    {errors.email && <p className="mt-1 text-sm text-red-400" data-testid="error-email">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-600 text-ice">Τηλέφωνο</label>
                    <input {...field("phone")} className={inputCls} placeholder="69XXXXXXXX" data-testid="contact-phone" />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-600 text-ice">Θέμα</label>
                  <Select value={form.subject} onValueChange={(v) => setForm((f) => ({ ...f, subject: v }))}>
                    <SelectTrigger className={inputCls} data-testid="contact-subject">
                      <SelectValue placeholder="Επιλέξτε θέμα" />
                    </SelectTrigger>
                    <SelectContent className="border-white/15 bg-royal text-ice">
                      {SUBJECTS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`subject-option-${s}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.subject && <p className="mt-1 text-sm text-red-400" data-testid="error-subject">{errors.subject}</p>}
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-600 text-ice">Μήνυμα</label>
                  <textarea {...field("message")} rows={5} className={inputCls} placeholder="Γράψτε το μήνυμά σας..." data-testid="contact-message" />
                  {errors.message && <p className="mt-1 text-sm text-red-400" data-testid="error-message">{errors.message}</p>}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-8 py-4 font-display text-lg font-700 uppercase tracking-wide text-white transition-all duration-200 hover:bg-brand/80 hover:shadow-[0_0_30px_rgba(60,115,192,0.6)] disabled:opacity-60"
                >
                  <Send size={18} /> {submitting ? "Αποστολή..." : "Αποστολή Μηνύματος"}
                </button>
              </div>
            </form>
          </Reveal>

          {/* DETAILS */}
          <Reveal delay={0.1}>
            <div className="flex h-full flex-col gap-5">
              <div className="rounded-2xl border border-white/10 bg-navy/60 p-7">
                <h2 className="font-heading text-2xl font-800 text-white">Στοιχεία Επικοινωνίας</h2>
                <ul className="mt-6 space-y-5 text-ice/85">
                  <li className="flex items-start gap-3">
                    <MapPin className="mt-0.5 text-gold" size={20} />
                    <span>{settings.address || "Αιγάλεω, Δυτική Αττική"}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Phone className="text-gold" size={20} />
                    <a href={`tel:${settings.phone}`} className="hover:text-white">{settings.phone || "6970523934"}</a>
                  </li>
                  <li className="flex items-center gap-3">
                    <Mail className="text-gold" size={20} />
                    <a href={`mailto:${settings.email}`} className="hover:text-white">{settings.email || "info@realdytikisattikis.gr"}</a>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="mt-0.5 text-gold" size={20} />
                    <span>{settings.training_hours || "Δευτέρα - Παρασκευή: 17:00 - 21:00"}</span>
                  </li>
                </ul>
                <div className="mt-7 flex gap-3">
                  {socials.map(({ key, Icon }) => (
                    <a
                      key={key}
                      href={settings[key] || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-royal/60 text-ice transition-all hover:-translate-y-1 hover:border-gold hover:text-gold"
                      data-testid={`contact-social-${key}`}
                    >
                      <Icon size={18} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* VENUE CARDS */}
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {[
            { title: "Γήπεδο Ποδοσφαίρου", name: settings.football_venue_name, addr: settings.football_venue_address },
            { title: "Κλειστό Μπάσκετ", name: settings.basketball_venue_name, addr: settings.basketball_venue_address },
          ].map((v) => (
            <Reveal key={v.title}>
              <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-royal/40 p-7 transition-all hover:border-gold/50">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-gold/15 text-gold">
                  <Trophy size={24} />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-800 text-white">{v.title}</h3>
                  <p className="mt-1 font-600 text-ice">{v.name || "-"}</p>
                  <p className="text-sm text-ice/70">{v.addr || "-"}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
